import * as React from "react";
import { CardBody, SectionHeader, Table, Tbody, Td, Th, Thead, Tr } from "../components";
import type { SubjectChoiceModel } from "./SubjectChoices";
import { FindingKinds } from "./subjectChoiceEvaluator";

/**
 * Read-only figures over every student in the grid, including unsaved edits: how many take each subject, the most
 * common subject combinations (useful for timetabling option lines), and how many students break each rule.
 */
export const SubjectChoiceSummaryTab: React.FC<{ model: SubjectChoiceModel }> = ({ model }) => {
    const { order, choices, subjects, subjectName, offering } = model;
    const offered = new Set(subjects.map((s) => s.moduleID));
    const chosen = (id: string) => (choices[id] ?? []).filter((m) => offered.has(m));

    const perSubject = subjects
        .map((s) => ({ name: s.name, count: order.filter((id) => chosen(id).includes(s.moduleID)).length }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    const widest = Math.max(1, ...perSubject.map((s) => s.count));

    const combinations = (() => {
        const counts = new Map<string, number>();
        order.forEach((id) => {
            const key = chosen(id).map(subjectName).sort().join(", ");
            if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
        });
        return Array.from(counts, ([subjectsLabel, count]) => ({ subjectsLabel, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
    })();

    const allFindings = order.map((id) => model.findings(id));
    const perRule = [
        ...offering.rules.map((rule) => ({
            name: rule.name,
            checked: model.activeRules.some((r) => r.id === rule.id),
            count: allFindings.filter((f) => f.some((x) => x.ruleID === rule.id)).length,
        })),
        ...(offering.corequisites.length
            ? [{ name: "Subjects that must be taken together", checked: true, count: allFindings.filter((f) => f.some((x) => x.kind === FindingKinds.Corequisite)).length }]
            : []),
    ];

    return (
        <CardBody>
            <p className="mb-6 text-sm text-text-weak">
                {`${order.length} student${order.length === 1 ? "" : "s"}, including changes not saved yet.`}
            </p>

            <SectionHeader title="Students per subject" />
            <Table>
                <Thead>
                    <tr>
                        <Th>Subject</Th>
                        <Th align="right">Students</Th>
                        <Th className="w-1/2" />
                    </tr>
                </Thead>
                <Tbody>
                    {perSubject.map((s) => (
                        <Tr key={s.name}>
                            <Td>{s.name}</Td>
                            <Td align="right">{s.count}</Td>
                            <Td>
                                <div className="h-2 rounded-full bg-surface-muted">
                                    <div className="h-2 rounded-full bg-primary" style={{ width: `${(s.count / widest) * 100}%` }} />
                                </div>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>

            <div className="mt-8">
                <SectionHeader title="Most common combinations" />
                {combinations.length === 0 ? (
                    <p className="text-sm text-text-weak">No subjects chosen yet.</p>
                ) : (
                    <Table>
                        <Thead>
                            <tr>
                                <Th>Subjects</Th>
                                <Th align="right">Students</Th>
                            </tr>
                        </Thead>
                        <Tbody>
                            {combinations.map((c) => (
                                <Tr key={c.subjectsLabel}>
                                    <Td className="whitespace-normal">{c.subjectsLabel}</Td>
                                    <Td align="right">{c.count}</Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                )}
            </div>

            <div className="mt-8">
                <SectionHeader title="Students breaking each rule" />
                {perRule.length === 0 ? (
                    <p className="text-sm text-text-weak">This grade has no subject choice rules.</p>
                ) : (
                    <Table>
                        <Thead>
                            <tr>
                                <Th>Rule</Th>
                                <Th align="right">Students</Th>
                            </tr>
                        </Thead>
                        <Tbody>
                            {perRule.map((r) => (
                                <Tr key={r.name}>
                                    <Td>
                                        {r.name}
                                        {!r.checked && <span className="ml-2 text-xs text-text-weak">(not checked this year)</span>}
                                    </Td>
                                    <Td align="right">
                                        <span className={r.count ? "font-medium text-danger-text" : "text-text-weak"}>{r.count}</span>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                )}
            </div>
        </CardBody>
    );
};
